import React from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

/**
 * QueryStatsCards Component
 *
 * Displays query statistics in card format.
 * Can be used to show various metrics like total queries, success rate, etc.
 */
const QueryStatsCards = ({ stats, loading = false, variant = 'general' }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // General query statistics
  if (variant === 'general') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Queries"
          value={stats.total_queries?.toLocaleString() || '0'}
          icon={ChartBarIcon}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />

        <StatCard
          title="Successful"
          value={stats.successful_queries?.toLocaleString() || '0'}
          icon={CheckCircleIcon}
          iconColor="text-green-600"
          bgColor="bg-green-50"
          subtitle={
            stats.total_queries > 0
              ? `${stats.success_rate?.toFixed(1) || 0}% success rate`
              : null
          }
        />

        <StatCard
          title="Failed"
          value={stats.failed_queries?.toLocaleString() || '0'}
          icon={XCircleIcon}
          iconColor="text-red-600"
          bgColor="bg-red-50"
        />

        <StatCard
          title="Avg Execution Time"
          value={
            stats.avg_execution_time_ms != null
              ? stats.avg_execution_time_ms < 1000
                ? `${stats.avg_execution_time_ms.toFixed(0)}ms`
                : `${(stats.avg_execution_time_ms / 1000).toFixed(2)}s`
              : 'N/A'
          }
          icon={ClockIcon}
          iconColor="text-purple-600"
          bgColor="bg-purple-50"
        />
      </div>
    );
  }

  // Cost statistics
  if (variant === 'cost') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Credits Used"
          value={stats.total_credits_used?.toFixed(4) || '0'}
          icon={CurrencyDollarIcon}
          iconColor="text-green-600"
          bgColor="bg-green-50"
          subtitle="Snowflake credits"
        />

        <StatCard
          title="Estimated Cost"
          value={
            stats.estimated_cost_usd != null
              ? `$${stats.estimated_cost_usd.toFixed(2)}`
              : '$0.00'
          }
          icon={CurrencyDollarIcon}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
          subtitle="USD (estimated)"
        />

        <StatCard
          title="Queries with Cost"
          value={stats.queries_with_cost?.toLocaleString() || '0'}
          icon={ChartBarIcon}
          iconColor="text-purple-600"
          bgColor="bg-purple-50"
        />

        <StatCard
          title="Avg Cost per Query"
          value={
            stats.queries_with_cost > 0 && stats.total_credits_used
              ? `${(stats.total_credits_used / stats.queries_with_cost).toFixed(6)}`
              : '0'
          }
          icon={ChartBarIcon}
          iconColor="text-orange-600"
          bgColor="bg-orange-50"
          subtitle="credits"
        />
      </div>
    );
  }

  // Performance statistics
  if (variant === 'performance') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Avg Execution Time"
          value={
            stats.avg_execution_time_ms != null
              ? `${stats.avg_execution_time_ms.toFixed(0)}ms`
              : 'N/A'
          }
          icon={ClockIcon}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />

        <StatCard
          title="P95 Latency"
          value={
            stats.p95_execution_time_ms != null
              ? `${stats.p95_execution_time_ms.toFixed(0)}ms`
              : 'N/A'
          }
          icon={BoltIcon}
          iconColor="text-yellow-600"
          bgColor="bg-yellow-50"
        />

        <StatCard
          title="P99 Latency"
          value={
            stats.p99_execution_time_ms != null
              ? `${stats.p99_execution_time_ms.toFixed(0)}ms`
              : 'N/A'
          }
          icon={BoltIcon}
          iconColor="text-orange-600"
          bgColor="bg-orange-50"
        />

        <StatCard
          title="Max Execution Time"
          value={
            stats.max_execution_time_ms != null
              ? stats.max_execution_time_ms < 1000
                ? `${stats.max_execution_time_ms.toFixed(0)}ms`
                : `${(stats.max_execution_time_ms / 1000).toFixed(2)}s`
              : 'N/A'
          }
          icon={ChartBarIcon}
          iconColor="text-red-600"
          bgColor="bg-red-50"
        />
      </div>
    );
  }

  return null;
};

// Reusable stat card component
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor, subtitle }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className={`h-10 w-10 rounded-full ${bgColor} flex items-center justify-center`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
      </div>
      <div className="flex items-baseline">
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
      </div>
      {subtitle && (
        <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
      )}
    </div>
  );
};

export default QueryStatsCards;
